/**
 * Das Anmeldeformular.
 *
 * Zwei Felder, eine primäre Aktion. Kein „Passwort vergessen", keine
 * Registrierung: Zugänge vergibt die Verwaltung (Feature 05), nicht der
 * Anmeldebildschirm.
 */

import { useState } from 'react'

import { useAuth } from '@/features/auth/auth-context'

const FIELD =
  'border-border bg-surface text-text rounded-card border px-3 ' +
  'focus-visible:border-accent transition-colors duration-(--duration-fast) ease-(--ease-out)'

export function LoginScreen() {
  const { signIn } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  async function submit() {
    setError(null)
    setIsSubmitting(true)
    try {
      await signIn(email, password)
      // Kein Weiterleiten: Mit der Sitzung verschwindet dieses Formular, weil
      // der `AuthProvider` ab jetzt die Anwendung rendert.
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Die Anmeldung ist fehlgeschlagen.')
      setIsSubmitting(false)
    }
  }

  return (
    <main className="flex h-dvh items-center justify-center px-(--page-x)">
      <form
        onSubmit={(event) => {
          event.preventDefault()
          void submit()
        }}
        data-testid="login-screen"
        aria-labelledby="login-heading"
        className="border-border bg-surface rounded-card flex w-full max-w-sm flex-col gap-4 border p-6"
      >
        <h1 id="login-heading" className="text-page font-(--font-weight-page)">
          Anmelden
        </h1>

        <label className="text-label text-muted flex flex-col gap-1" htmlFor="login-email">
          E-Mail
          <input
            id="login-email"
            data-testid="login-email"
            type="email"
            name="email"
            autoComplete="email"
            required
            // eslint-disable-next-line jsx-a11y/no-autofocus -- `design-system` verlangt Autofokus auf das erste Feld. Der Einwand der Regel (der Fokus springt an einer unerwarteten Stelle) trifft hier nicht: Dieser Bildschirm besteht aus nichts anderem als diesem Formular.
            autoFocus
            value={email}
            onChange={(event) => {
              setEmail(event.target.value)
            }}
            className={`text-body text-text ${FIELD}`}
          />
        </label>

        <label className="text-label text-muted flex flex-col gap-1" htmlFor="login-password">
          Passwort
          <input
            id="login-password"
            data-testid="login-password"
            type="password"
            name="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => {
              setPassword(event.target.value)
            }}
            className={`text-body text-text ${FIELD}`}
          />
        </label>

        {error !== null && (
          <p data-testid="login-error" role="alert" className="text-label text-status-overdue">
            {error}
          </p>
        )}

        <button
          type="submit"
          data-testid="login-submit"
          disabled={isSubmitting}
          aria-busy={isSubmitting}
          /* Die eine primäre Aktion dieser Ansicht — und die einzige Stelle,
           * an der der Akzent als Fläche auftritt. */
          className="bg-accent rounded-card text-body text-accent-on transition-opacity duration-(--duration-fast) ease-(--ease-out) disabled:opacity-60"
        >
          Anmelden
        </button>
      </form>
    </main>
  )
}
